import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { Sparkles, RefreshCw, BookOpen, ChevronRight } from "lucide-react";

export default function TodaysInsight({ onPracticeClick }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTodaysInsight();
  }, []);

  const fetchTodaysInsight = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try to get AI-rephrased version first, fallback to base insight
      try {
        const response = await axios.get(`${API}/maya-manthan/todays-insight/rephrased`);
        setInsight(response.data);
      } catch (rephrasedError) {
        // Fallback to base insight if rephrasing fails
        const response = await axios.get(`${API}/maya-manthan/todays-insight`);
        setInsight(response.data);
      }
    } catch (err) {
      console.error("Error fetching insight:", err);
      setError("Unable to load today's insight");
      // Set a default insight as fallback
      setInsight({
        insight: "You are not your thoughts",
        meaning: "Thoughts arise and pass. You remain.",
        practice: "Observe thoughts without reacting.",
        tags: { mood: ["calm"], chakra: ["third_eye"], guna: ["sattva"], level: ["beginner"] }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a1a3a]/80 to-[#0d0d2b]/60 backdrop-blur-xl border border-amber-500/20 p-8"
      >
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-2xl"
      data-testid="todays-insight"
    >
      {/* Background with sacred geometry pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a3a]/90 via-[#0d0d2b]/80 to-[#0a0a1a]/90 backdrop-blur-xl" />
      
      {/* Ambient glow effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl" />
        
        {/* Subtle sacred geometry SVG */}
        <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 400 300">
          <circle cx="200" cy="150" r="100" fill="none" stroke="currentColor" className="text-amber-500" strokeWidth="0.5" />
          <circle cx="200" cy="150" r="80" fill="none" stroke="currentColor" className="text-amber-500" strokeWidth="0.5" />
          <circle cx="200" cy="150" r="60" fill="none" stroke="currentColor" className="text-amber-500" strokeWidth="0.5" />
          {[...Array(12)].map((_, i) => (
            <line
              key={i}
              x1="200"
              y1="150"
              x2={200 + 100 * Math.cos((i * 30 * Math.PI) / 180)}
              y2={150 + 100 * Math.sin((i * 30 * Math.PI) / 180)}
              stroke="currentColor"
              className="text-amber-500"
              strokeWidth="0.3"
            />
          ))}
        </svg>
      </div>

      {/* Border glow */}
      <div className="absolute inset-0 rounded-2xl border border-amber-500/30" />
      <div className="absolute inset-[1px] rounded-2xl border border-amber-500/10" />

      {/* Content */}
      <div className="relative z-10 p-8 text-center">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center justify-center gap-2 mb-6"
        >
          <Sparkles className="w-5 h-5 text-amber-400" />
          <h2 className="text-amber-400 text-sm tracking-widest uppercase font-medium">
            Today's Insight for You
          </h2>
          <Sparkles className="w-5 h-5 text-amber-400" />
        </motion.div>

        {/* Main Insight */}
        <AnimatePresence mode="wait">
          <motion.div
            key={insight?.insight}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
          >
            {/* Insight Quote */}
            <div className="mb-6">
              <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-tight">
                "{insight?.insight}"
              </p>
            </div>

            {/* Meaning */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-6"
            >
              <p className="text-gray-300/80 text-lg md:text-xl font-light italic">
                {insight?.meaning}
              </p>
            </motion.div>

            {/* Practice - Action focused */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-amber-500/10 border border-amber-500/30"
            >
              <BookOpen className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 font-medium">
                Practice: {insight?.practice}
              </span>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Tags indicator (subtle) */}
        {insight?.user_state && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 flex items-center justify-center gap-2 flex-wrap"
          >
            <span className="text-gray-500 text-xs">Personalized for your</span>
            {insight.user_state.mood && (
              <span className="px-2 py-0.5 rounded-full bg-white/5 text-gray-400 text-xs capitalize">
                {insight.user_state.mood} mind
              </span>
            )}
            {insight.user_state.chakras?.[0] && (
              <span className="px-2 py-0.5 rounded-full bg-white/5 text-gray-400 text-xs capitalize">
                {insight.user_state.chakras[0].replace('_', ' ')} focus
              </span>
            )}
          </motion.div>
        )}

        {/* Link to practice */}
        {onPracticeClick && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            onClick={onPracticeClick}
            className="mt-6 inline-flex items-center gap-2 text-amber-400/70 hover:text-amber-400 transition-colors text-sm"
          >
            Begin your practice
            <ChevronRight className="w-4 h-4" />
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}
